
import React, { useState, useEffect, useRef } from "react";
import {
    Box,
    Typography,
    Toolbar,
    InputAdornment,
    IconButton,
    Menu,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableSortLabel,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Button,
    Tooltip,
    List,
    ListItem,
    Dialog,
    DialogTitle,
    DialogContent,
    Slide,
    Fade,
    Grow,
    Zoom,
    Collapse,
    CircularProgress,
    Paper
} from "@mui/material";
import {ContainerClasses} from '../../themes/containerClasses';
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import {useTheme} from '@mui/material/styles';
import {ListViewModel,
        ListViewColumnModel,
        EmailPreviewModel,
        EmailConnectorModel,
        EmailRecipientModel,
        EmailTemplateModel,
        EmailMessageModel,
        RecordModel} from '../../models/models';
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import HelpIcon from "@mui/icons-material/Help";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import {SearchFieldComponent} from "../../components/searchField/SearchField";
import {EmailFromComponent} from "./EmailFrom";
import {EmailToComponent} from "./EmailTo";
import {EmailSubjectComponent} from "./EmailSubject";
import {EmailTemplateComponent} from "./EmailTemplate";
import {EmailBodyComponent} from "./EmailBody";
import {EmailPreviewComponent} from "./EmailPreview";
import {useSnackbar} from "notistack";
import { formatDate, formatDatetime } from "../../utils/dateFunctions";
import {Link, useParams} from 'react-router-dom';
import {getAppName, getObjectName } from "../../utils/helperRouter";
import { useAuth } from "../../hooks/useAuth";
import { fetchEmailConnectors,
         fetchEmailTemplate,
         fetchEmailPreviews,
         sendEmailMessages } from "../../services/emailApi";


export const EmailClientComponent: React.FC<{
  emailConnectors: EmailConnectorModel[],
  emailMessages: EmailMessageModel[]
}> = ({
  emailConnectors,
  emailMessages
}) => {

  let {appName, objectName, recordUuid} = useParams();

  const {accessToken, onInvalidToken} = useAuth();

  const styles = ContainerClasses();

  const { enqueueSnackbar } = useSnackbar();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [availableEmailConnectors, setAvailableEmailConnectors] = useState<EmailConnectorModel[]>([]);
  const [allEmailMessages, setAllEmailMessages] = useState<EmailMessageModel[]>([]);

  const [openPreviewDialog, setOpenPreviewDialog] = useState<boolean>(false);
  const [allEmailPreviews, setAllEmailPreviews] = useState<EmailPreviewModel[]>([]);

  const [isSending, setIsSending] = useState<boolean>(false)
  const [isFetching, setIsFetching] = useState<boolean>(false)

  useEffect(() => {
    if(isSending || isFetching){
      document.body.style.cursor = "wait";
    } else {
      document.body.style.cursor = "default";
    }


  }, [isSending, isFetching]);

  useEffect(() => {
    setAllEmailMessages(emailMessages)
  }, [emailMessages])

  useEffect(() => {
    setAvailableEmailConnectors(emailConnectors);
  }, [emailConnectors])

  useEffect(() => {
    if(errorMessage){
     enqueueSnackbar(errorMessage, {
          variant: "error",
       });
    setErrorMessage(null)
    }

    if(successMessage){
     enqueueSnackbar(successMessage, {
          variant: "success",
       });
    setSuccessMessage(null)
    }
  }, [errorMessage, successMessage]); // eslint-disable-line react-hooks/exhaustive-deps



  const handleChangeEmailConnector = (event: any) => {
    const thisUuid = event.target.value;
    const thisEmailConnector = availableEmailConnectors.find((connector: EmailConnectorModel) => connector.Uuid_sys ===thisUuid);
    if(thisEmailConnector){
      setAllEmailMessages(previous =>
        previous.map((emailMessage: EmailMessageModel) => {
          const updatedMessage = emailMessage.deepCopy();
          updatedMessage.setSender(thisEmailConnector)
          return updatedMessage
        })
      )
    }
  }

  const addEmailRecipient = (newRecipient: EmailRecipientModel) => {
    //check if there is already any recipient with this email address

    const thisRecipient = allEmailMessages.find((thisMessage: EmailMessageModel) => thisMessage.recipient.email === newRecipient.email);
    if(thisRecipient) return

    if(allEmailMessages.length === 0){
      setErrorMessage("Unhandled error: messages not properly defined")
      return
    }

    const newMessage = allEmailMessages[0].deepCopy()
    newMessage.setRecipient(newRecipient)
    setAllEmailMessages(previous => [...previous, newMessage]);

  }


  const removeEmailRecipient = (thisRecipient: EmailRecipientModel) => {
    //always one email recipient is required
    if(allEmailMessages.length <= 1){
      setErrorMessage("Error: at least one recipient needs to be defined always")
      return
    }
    setAllEmailMessages(previous => {
        return previous.filter((thisMessage: EmailMessageModel) => thisMessage.recipient.email !== thisRecipient.email);
      });
  }

  const handleChangeSubject = (event: any) => {
      const subject = event.target.value;

      setAllEmailMessages(previous =>
        previous.map((emailMessage: EmailMessageModel) => {
          const updatedMessage = emailMessage.deepCopy();
          updatedMessage.setSubject(subject)
          return updatedMessage
        })
      )
  }

  const handleChangeBody = (newValue: string) => {
      setAllEmailMessages(previous =>
        previous.map((emailMessage: EmailMessageModel) => {
          const updatedMessage = emailMessage.deepCopy();
          updatedMessage.setBody(newValue)
          return updatedMessage
        })
      )
  }

  const handleChangeEmailTemplate = (selectedUuid: string) => {
    const objectApiName = getObjectName(objectName)
    fetchEmailTemplate(accessToken, objectApiName, selectedUuid)
        .then((fetchedTemplate) => {
          setAllEmailMessages(previous =>
            previous.map((emailMessage: EmailMessageModel) => {
              const updatedMessage = emailMessage.deepCopy();
              updatedMessage.setEmailTemplate(fetchedTemplate)
              return updatedMessage
           })
         )
        })
        .catch((err) => {
          setErrorMessage(err.message)
        })
  }

  const isLoading = () => {
    if(emailMessages.length === 0) return true
    return false
  }

  const getSelectedEmailConnector = () => {
    return allEmailMessages[0].sender
  }
  const getSubject = () => {
    return allEmailMessages[0].subject
  }
  const getBody = () => {
    return allEmailMessages[0].body
  }

  const getEmailRecipients = () => {
    return allEmailMessages.map((thisMessage: EmailMessageModel) => thisMessage.recipient)
  }
  const getEmailTemplate = () => {
    return allEmailMessages[0].template
  }


  const handlePreview = () => {

    setIsFetching(true)

    fetchEmailPreviews(accessToken, allEmailMessages)
        .then((emailPreviews) => {
          setAllEmailPreviews(emailPreviews);
          setOpenPreviewDialog(true);
          setIsFetching(false)
        })
        .catch((err) => {
          setErrorMessage(err.message)
          setIsFetching(false)
        })

  }

  const handleSend = () => {
    setIsSending(true)
    sendEmailMessages(accessToken, allEmailMessages)
        .then((results) => {
          setSuccessMessage(`Email sent to ${results}`)
          setIsSending(false)
        })
        .catch((err) => {
          setErrorMessage(err.message)
          setIsSending(false)
        })
  }


  const onClosePreviewDialog = (event: any) => {
    setOpenPreviewDialog(false)
  }

  return (

    <Paper
      elevation={3}
      sx={{
        margin: '2rem auto',
        overflow: 'hidden'
      }}
    >

      {/* PREVIEW DIALOG */}
      <Dialog
          open={openPreviewDialog}
          onClose={onClosePreviewDialog}
          fullWidth
          maxWidth={'lg'}
          TransitionComponent={Slide}
          TransitionProps={{direction: "left"} as any}
          PaperProps={{
            sx: {
              display: 'flex',
              flexDirection: 'column',
              height: '70vh'
            }
          }}
      >
        <DialogTitle>
          <Toolbar sx={{justifyContent: 'space-between'}}>
            <Typography variant="h6">
              Email Preview
            </Typography>

            <IconButton
              edge="start"
              color="inherit"
              onClick={onClosePreviewDialog}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </DialogTitle>

        <Box sx={{height: '100%'}}>
          <DialogContent>
            <EmailPreviewComponent
              emailPreviews = {allEmailPreviews}
            />
          </DialogContent>
        </Box>
      </Dialog>



      {/* EMAIL DEFINITION DIALOG */}
      <Box sx={{
                padding: 2,
                backgroundColor: 'white',
                borderBottom: '1px solid grey'}}>

        <Box sx={styles.containerRowB}>
          <Typography
            variant="h6"
            gutterBottom
          >
             {allEmailMessages.length > 0 && getSubject()}
          </Typography>
          <Box
            sx={{
              display: 'flex'
            }}
          >
          <MailOutlineIcon
            sx={{position: 'relative',
                 top: '-0.3rem',
                 fontSize: '2.5rem'}}
           />

           <Tooltip
             title={
               <Box>
               <Typography>
                 Please note the following:
               </Typography>
               <List>
                 <ListItem>
                  - Dynamic recipient is ALWAYS injected via {"{{recipient}}"}
                 </ListItem>
                 <ListItem>
                  - Dynamic sender is ALWAYS injected via {"{{sender}}"}
                 </ListItem>
               </List>
               </Box>

             }
           >
           <InfoOutlinedIcon sx={{
             position: 'relative',
             top: '-0.5rem',
             fontSize: '1.2rem'
           }}
           />
           </Tooltip>
          </Box>

        </Box>

      </Box>

  {!isLoading() &&
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      gap: "2"
    }}
  >

    <Box>
     {allEmailMessages.length > 0 &&
      <EmailFromComponent
        selectedEmailConnector = {getSelectedEmailConnector()}
        emailConnectors = {availableEmailConnectors}
        onChange = {handleChangeEmailConnector}
      />
    }
    </Box>

    <Box>
      <EmailToComponent
        emailRecipients={getEmailRecipients()}
        onAdd={addEmailRecipient}
        onRemove={removeEmailRecipient}
      />
    </Box>

    <Box>
      {allEmailMessages.length > 0 &&
       <EmailSubjectComponent
         subject = {getSubject()}
         onChange = {handleChangeSubject}
       />
      }
    </Box>

    <Box>
      {allEmailMessages.length > 0 &&
        <EmailBodyComponent
          body = {getBody()}
          onChange = {handleChangeBody}
        />
      }
    </Box>

    <Box>
      {allEmailMessages.length > 0 &&
      <EmailTemplateComponent
        emailTemplate={getEmailTemplate()}
        onChange={handleChangeEmailTemplate}
      />
    }
    </Box>

    {/* PREVIEW AND SEND ACTIONS */}
   <Box sx={{...styles.containerRowD,
             pt: '1rem',
             pl: '1rem',
             pr: '1rem',
             pb: '1rem',
             gap: '1rem'}}>

      <Button
        variant="contained"
        color="primary"
        onClick={handlePreview}
        disabled={isSending || isFetching}
      >
        <Typography>Preview</Typography>
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={handleSend}
        disabled={isSending || isFetching}
      >
        {!isSending &&
        <Typography>Send</Typography>
        }
        {isSending &&
          <CircularProgress size={20}/>
        }
      </Button>
    </Box>

    </Box>
  }

    </Paper>
  )
};
